import type { Firestore } from 'firebase-admin/firestore'
import { FieldValue } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import type { SeasonConfig, User } from '@my-roadtrip/types'
import { safeJsonParse } from '../../core/database/redis.js'
import EventEmitter from 'node:events'
import { createLogger } from '../../common/utils/logger.js'
import type { FirestoreDoc, FirestoreDocLike, FirestoreTransaction } from '../../common/types/firestore.js'
import { toDateOrNow } from '../../common/utils/normalize.js'

const logger = createLogger('SeasonService')

type SeasonConfigCachePayload = Omit<SeasonConfig, 'startDate' | 'endDate' | 'createdAt' | 'updatedAt'> & {
  startDate: string
  endDate: string
  createdAt: string
  updatedAt: string
}

function materializeSeasonConfig(doc: FirestoreDocLike): SeasonConfig {
  const raw = doc.data() as Record<string, unknown>

  return {
    id: typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : doc.id,
    seasonId: typeof raw.seasonId === 'string' && raw.seasonId.length > 0 ? raw.seasonId : doc.id,
    name: typeof raw.name === 'string' ? raw.name : 'Season',
    description: typeof raw.description === 'string' ? raw.description : '',
    startDate: toDateOrNow(raw.startDate),
    endDate: toDateOrNow(raw.endDate),
    maxLevel: typeof raw.maxLevel === 'number' ? raw.maxLevel : 100,
    xpPerLevel: typeof raw.xpPerLevel === 'number' ? raw.xpPerLevel : 1000,
    rewards: Array.isArray(raw.rewards) ? (raw.rewards as SeasonConfig['rewards']) : [],
    vanActions: Array.isArray(raw.vanActions) ? (raw.vanActions as SeasonConfig['vanActions']) : [],
    active: Boolean(raw.active),
    createdAt: toDateOrNow(raw.createdAt),
    updatedAt: toDateOrNow(raw.updatedAt)
  }
}

function serializeSeasonConfig(config: SeasonConfig): SeasonConfigCachePayload {
  return {
    ...config,
    startDate: config.startDate.toISOString(),
    endDate: config.endDate.toISOString(),
    createdAt: config.createdAt.toISOString(),
    updatedAt: config.updatedAt.toISOString()
  }
}

function deserializeSeasonConfig(payload: SeasonConfigCachePayload): SeasonConfig {
  const safeDate = (value: string) => {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed
  }

  return {
    ...payload,
    startDate: safeDate(payload.startDate),
    endDate: safeDate(payload.endDate),
    createdAt: safeDate(payload.createdAt),
    updatedAt: safeDate(payload.updatedAt)
  }
}

/**
 * Season System Service
 * 
 * Manages seasonal battle pass progression:
 * - 100 levels per season
 * - XP progression (1000 XP per level by default)
 * - Rewards every 5 levels (points, badges, items)
 * - Van daily bonus actions (XP boosts with cooldowns)
 * - Season rotation (start/end dates)
 * 
 * Architecture:
 * - User season data stored in users/{userId}.season
 * - Season configs stored in seasonConfigs/{seasonId}
 * - Active season cached in Redis (1h TTL)
 * - User progression cached (5 min TTL)
 */
export class SeasonService {
  private xpEventHandler?: (data: { userId: string; xpAmount: number; reason: string }) => Promise<void>

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient,
    private readonly events: EventEmitter
  ) {}

  /**
   * Initialize event listeners for Season XP
   * Called once during module initialization
   */
  initializeEventListeners(): void {
    logger.info('Initializing Season event listeners')
    // Listen for season XP add events from quests/challenges
    this.xpEventHandler = async (data: { userId: string; xpAmount: number; reason: string }) => {
      try {
        logger.debug('Adding season XP', { userId: data.userId, xpAmount: data.xpAmount, reason: data.reason })
        await this.addSeasonXP(data.userId, data.xpAmount, data.reason)
      } catch (error) {
        logger.error('Failed to add season XP', { userId: data.userId, error: (error as Error).message })
      }
    }
    this.events.on('season:xp:add', this.xpEventHandler)
  }

  /**
   * Cleanup event listeners (call on shutdown)
   */
  destroy(): void {
    if (this.xpEventHandler) {
      this.events.off('season:xp:add', this.xpEventHandler)
      logger.info('Season event listeners cleaned up')
    }
  }

  // ========== SEASON CONFIG MANAGEMENT ==========

  /**
   * Get active season configuration
   * Cached in Redis for 1 hour
   */
  async getActiveSeason(): Promise<SeasonConfig | null> {
    logger.debug('Fetching active season')
    const cacheKey = 'season:active'
    const cached = await this.redis.get(cacheKey)
    const parsed = safeJsonParse<SeasonConfigCachePayload>(cached, cacheKey)
    if (parsed) {
      logger.debug('Active season cache hit')
      return deserializeSeasonConfig(parsed)
    }

    logger.debug('Active season cache miss, querying Firestore')
    // Query Firestore for active season
    const now = new Date()
    const snapshot = await this.firestore
      .collection('seasonConfigs')
      .where('active', '==', true)
      .where('startDate', '<=', now)
      .where('endDate', '>=', now)
      .limit(1)
      .get()

    if (snapshot.empty) {
      logger.warn('No active season found')
      return null
    }

    const seasonConfig = materializeSeasonConfig(snapshot.docs[0] as FirestoreDoc)
    logger.info('Active season fetched', { seasonId: seasonConfig.id, name: seasonConfig.name })

    // Cache for 1 hour
    await this.redis.setex(cacheKey, 3600, JSON.stringify(serializeSeasonConfig(seasonConfig)))

    return seasonConfig
  }

  /**
   * Get season config by ID
   * Cached in Redis for 1 hour
   */
  async getSeasonById(seasonId: string): Promise<SeasonConfig | null> {
    logger.debug('Fetching season by ID', { seasonId })
    const cacheKey = `season:${seasonId}`
    const cached = await this.redis.get(cacheKey)
    const parsed = safeJsonParse<SeasonConfigCachePayload>(cached, cacheKey)
    if (parsed) {
      logger.debug('Season cache hit', { seasonId })
      return deserializeSeasonConfig(parsed)
    }

    logger.debug('Season cache miss', { seasonId })
    const doc = await this.firestore
      .collection('seasonConfigs')
      .doc(seasonId)
      .get()

    if (!doc.exists) {
      logger.warn('Season not found', { seasonId })
      return null
    }

    const seasonConfig = materializeSeasonConfig(doc)
    logger.info('Season fetched', { seasonId, name: seasonConfig.name })

    // Cache for 1 hour
    await this.redis.setex(cacheKey, 3600, JSON.stringify(serializeSeasonConfig(seasonConfig)))

    return seasonConfig
  }

  // ========== USER SEASON PROGRESSION ==========

  /**
   * Get user's current season progression
   * Includes level, XP, rewards claimed, van action cooldowns
   */
  async getUserSeasonProgress(userId: string): Promise<User['season'] & { seasonConfig: SeasonConfig | null }> {
    logger.debug('Fetching user season progress', { userId })
    // Get user profile
    const userDoc = await this.firestore.collection('users').doc(userId).get()
    if (!userDoc.exists) {
      logger.warn('User not found for season progress', { userId })
      throw new Error('User not found')
    }

    const user = userDoc.data() as User
    const activeSeason = await this.getActiveSeason()

    // Initialize season data if doesn't exist or season changed
    if (!user.season || !activeSeason || user.season.currentSeasonId !== activeSeason.seasonId) {
      const defaultSeason: User['season'] = {
        currentSeasonId: activeSeason?.seasonId || 'none',
        level: 1,
        xp: 0,
        totalXp: 0,
        claimedRewards: [],
        lastVanAction: undefined,
        vanActionCooldowns: {}
      }

      await this.firestore.collection('users').doc(userId).update({
        season: defaultSeason,
        updatedAt: new Date()
      })

      return { ...defaultSeason, seasonConfig: activeSeason }
    }

    return { ...user.season, seasonConfig: activeSeason }
  }

  /**
   * Add XP to user's season progression with automatic level-ups
   * 
   * Features:
   * - Atomic Firestore transaction
   * - Automatic level-up calculation
   * - Max level capping
   * - Cache invalidation
   * - Error handling with retry
   * 
   * @param userId - User ID
   * @param xpAmount - XP to add (must be positive)
   * @param reason - Reason for XP gain (for logging/analytics)
   * @returns Updated season progress
   * @throws Error if xpAmount <= 0, no active season, or user not found
   * 
   * @example
   * ```typescript
   * const progress = await seasonService.addSeasonXP(
   *   'user123',
   *   50,
   *   'quest_completion'
   * )
   * logger.log(`Level ${progress.level}, XP ${progress.xp}`)
   * ```
   */
  async addSeasonXP(userId: string, xpAmount: number, reason: string): Promise<User['season']> {
    logger.debug('Adding season XP', { userId, xpAmount, reason })
    // Input validation
    if (!userId || typeof userId !== 'string') {
      logger.warn('Invalid userId for XP addition', { userId })
      throw new Error('Invalid userId')
    }
    if (xpAmount <= 0 || !Number.isFinite(xpAmount)) {
      logger.warn('Invalid XP amount', { userId, xpAmount })
      throw new Error('XP amount must be a positive finite number')
    }
    if (xpAmount > 10000) {
      logger.warn('Large XP amount detected', { xpAmount, userId })
    }

    // Rate limiting: max 20 XP awards per minute (prevent farming)
    const rateLimitKey = `xp:ratelimit:${userId}`
    const xpCount = await this.redis.incr(rateLimitKey)
    if (xpCount === 1) {
      await this.redis.expire(rateLimitKey, 60) // 60 seconds TTL
    }
    if (xpCount > 20) {
      throw new Error(`XP rate limit exceeded (max 20 awards per minute)`)
    }

    const activeSeason = await this.getActiveSeason()
    if (!activeSeason) {
      throw new Error('No active season')
    }

    // Use transaction for atomicity
    const userRef = this.firestore.collection('users').doc(userId)
    
    try {
      const updatedSeason = await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
        const userDoc = await transaction.get(userRef)
        if (!userDoc.exists) {
          throw new Error('User not found')
        }

        const user = userDoc.data() as User
        const currentSeason = user.season || {
          currentSeasonId: activeSeason.seasonId,
          level: 1,
          xp: 0,
          totalXp: 0,
          claimedRewards: [],
          vanActionCooldowns: {}
        }

        // Calculate new XP and level
        let newXp = currentSeason.xp + xpAmount
        let newLevel = currentSeason.level
        const xpPerLevel = activeSeason.xpPerLevel || 1000
        const maxLevel = activeSeason.maxLevel || 100

        // Level up loop
        while (newXp >= xpPerLevel && newLevel < maxLevel) {
          newXp -= xpPerLevel
          newLevel++
        }

        // Cap XP at max level
        if (newLevel >= maxLevel) {
          newLevel = maxLevel
          newXp = 0
        }

        const updatedSeasonData: User['season'] = {
          ...currentSeason,
          level: newLevel,
          xp: newXp,
          totalXp: currentSeason.totalXp + xpAmount
        }

        // Update in transaction
        transaction.update(userRef, {
          season: updatedSeasonData,
          updatedAt: new Date()
        })

        return updatedSeasonData
      })

      // Invalidate cache after successful transaction
      await this.redis.del(`profile:${userId}`)

      logger.info('User gained Season XP', {
        userId,
        xpAmount,
        reason,
        newLevel: updatedSeason.level,
        newXp: updatedSeason.xp,
        xpPerLevel: activeSeason.xpPerLevel
      })

      return updatedSeason
    } catch (error) {
      logger.error('Failed to add Season XP', { userId, error })
      throw error
    }
  }

  /**
   * Claim reward at specific level
   * Validates level reached and reward not already claimed
   */
  async claimReward(userId: string, rewardLevel: number): Promise<{ success: boolean; reward: SeasonConfig['rewards'][0] | null }> {
    const activeSeason = await this.getActiveSeason()
    if (!activeSeason) {
      throw new Error('No active season')
    }

    const userDoc = await this.firestore.collection('users').doc(userId).get()
    if (!userDoc.exists) {
      throw new Error('User not found')
    }

    const user = userDoc.data() as User
    const currentSeason = user.season

    if (!currentSeason) {
      throw new Error('User has no season data')
    }

    // Validate level reached
    if (currentSeason.level < rewardLevel) {
      throw new Error(`Level ${rewardLevel} not reached yet (current: ${currentSeason.level})`)
    }

    // Check if already claimed
    if (currentSeason.claimedRewards?.includes(rewardLevel)) {
      throw new Error(`Reward at level ${rewardLevel} already claimed`)
    }

    // Find reward in season config
    const reward = activeSeason.rewards.find(r => r.level === rewardLevel)
    if (!reward) {
      throw new Error(`No reward defined at level ${rewardLevel}`)
    }

    // Update claimed rewards
    const updatedClaimedRewards = [...(currentSeason.claimedRewards || []), rewardLevel]

    await this.firestore.collection('users').doc(userId).update({
      'season.claimedRewards': updatedClaimedRewards,
      updatedAt: new Date()
    })

    // Apply reward (points, badge, etc.)
    if (reward.type === 'points') {
      await this.firestore.collection('users').doc(userId).update({
        'gamification.points': FieldValue.increment(reward.value as number)
      })
    }

    // Invalidate cache
    await this.redis.del(`profile:${userId}`)

    logger.info('User claimed season reward', { userId, rewardLevel, rewardType: reward.type, rewardValue: reward.value })

    return { success: true, reward }
  }

  /**
   * Get unclaimed rewards for user
   * Returns rewards user is eligible for but hasn't claimed yet
   */
  async getUnclaimedRewards(userId: string): Promise<SeasonConfig['rewards']> {
    const progress = await this.getUserSeasonProgress(userId)
    const { seasonConfig } = progress

    if (!seasonConfig) return []

    const claimedRewards = progress.claimedRewards || []
    return seasonConfig.rewards.filter(
      reward => reward.level <= progress.level && !claimedRewards.includes(reward.level)
    )
  }

  // ========== VAN DAILY BONUS ACTIONS ==========

  /**
   * Perform van bonus action (daily XP boost)
   * Checks cooldown and applies XP bonus
   * 
   * @param userId - User ID
  /**
   * Perform van bonus action with atomic cooldown management
   * 
   * Features:
   * - Atomic transaction (cooldown check + XP award + cooldown set)
   * - Race condition prevention
   * - Input validation
   * - Cache invalidation
   * 
   * @param userId - User ID
   * @param actionId - Van action ID from season config
   * @returns XP gained and cooldown end time
   * @throws Error if action not found, user not found, or on cooldown
   * 
   * @example
   * ```typescript
   * const result = await seasonService.performVanAction(
   *   'user123',
   *   'morning_coffee'
   * )
   * logger.log(`Gained ${result.xpGained} XP, next available at ${result.cooldownUntil}`)
   * ```
   */
  async performVanAction(
    userId: string,
    actionId: string
  ): Promise<{ success: boolean; xpGained: number; cooldownUntil: Date }> {
    // Input validation
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid userId')
    }
    if (!actionId || typeof actionId !== 'string') {
      throw new Error('Invalid actionId')
    }

    const activeSeason = await this.getActiveSeason()
    if (!activeSeason) {
      throw new Error('No active season')
    }

    // Find action in season config
    const vanAction = activeSeason.vanActions.find(a => a.id === actionId)
    if (!vanAction) {
      throw new Error(`Van action ${actionId} not found in season config`)
    }

    const userRef = this.firestore.collection('users').doc(userId)

    try {
      // Use transaction for atomicity (prevents race conditions)
      const result = await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
        const userDoc = await transaction.get(userRef)
        if (!userDoc.exists) {
          throw new Error('User not found')
        }

        const user = userDoc.data() as User
        const currentSeason = user.season || {
          currentSeasonId: activeSeason.seasonId,
          level: 1,
          xp: 0,
          totalXp: 0,
          claimedRewards: [],
          vanActionCooldowns: {}
        }

        // Check cooldown
        const now = new Date()
        const cooldowns = currentSeason.vanActionCooldowns || {}
        const cooldownEnd = cooldowns[actionId] ? new Date(cooldowns[actionId]) : null

        if (cooldownEnd && cooldownEnd > now) {
          const remainingMs = cooldownEnd.getTime() - now.getTime()
          const remainingHours = Math.ceil(remainingMs / (1000 * 60 * 60))
          throw new Error(`Van action on cooldown. Available in ${remainingHours}h`)
        }

        // Calculate new cooldown
        const xpGained = vanAction.xpBonus
        const newCooldownEnd = new Date(now.getTime() + vanAction.cooldown * 60 * 60 * 1000)
        const updatedCooldowns = { ...cooldowns, [actionId]: newCooldownEnd }

        // Update cooldown in transaction (before XP to prevent double-claim)
        transaction.update(userRef, {
          'season.vanActionCooldowns': updatedCooldowns,
          'season.lastVanAction': now,
          updatedAt: now
        })

        return { xpGained, newCooldownEnd }
      })

      // Award XP after successful cooldown update (separate transaction to avoid conflicts with addSeasonXP)
      await this.addSeasonXP(userId, result.xpGained, `van_action:${actionId}`)

      // Invalidate cache
      await this.redis.del(`profile:${userId}`)

      logger.info('User performed van action', { userId, actionId, xpGained: result.xpGained, cooldownUntil: result.newCooldownEnd.toISOString() })

      return {
        success: true,
        xpGained: result.xpGained,
        cooldownUntil: result.newCooldownEnd
      }
    } catch (error) {
      logger.error('Failed to perform van action', { userId, actionId, error: (error as Error).message })
      throw error
    }
  }

  /**
   * Get available van actions with cooldown status
   */
  async getVanActions(userId: string): Promise<Array<SeasonConfig['vanActions'][0] & { onCooldown: boolean; availableAt?: Date }>> {
    const activeSeason = await this.getActiveSeason()
    if (!activeSeason) {
      return []
    }

    const userDoc = await this.firestore.collection('users').doc(userId).get()
    if (!userDoc.exists) {
      throw new Error('User not found')
    }

    const user = userDoc.data() as User
    const cooldowns = user.season?.vanActionCooldowns || {}
    const now = new Date()

    return activeSeason.vanActions.map(action => {
      const cooldownEnd = cooldowns[action.id] ? new Date(cooldowns[action.id]) : null
      const onCooldown = cooldownEnd ? cooldownEnd > now : false

      return {
        ...action,
        onCooldown,
        availableAt: onCooldown && cooldownEnd ? cooldownEnd : undefined
      }
    })
  }

  // ========== SEASON LEADERBOARD ==========

  /**
   * Get season leaderboard (top users by total XP)
   * Paginated with cursor
   */
  async getSeasonLeaderboard(
    seasonId: string,
    limit = 50,
    lastUserId?: string
  ): Promise<Array<{ userId: string; displayName: string; level: number; totalXp: number; rank: number }>> {
    let query = this.firestore
      .collection('users')
      .where('season.currentSeasonId', '==', seasonId)
      .orderBy('season.totalXp', 'desc')
      .limit(limit)

    if (lastUserId) {
      const lastDoc = await this.firestore.collection('users').doc(lastUserId).get()
      query = query.startAfter(lastDoc)
    }

    const snapshot = await query.get()

    return snapshot.docs.map((doc: FirestoreDoc, index: number) => {
      const user = doc.data() as User
      return {
        userId: doc.id,
        displayName: user.displayName,
        level: user.season?.level || 1,
        totalXp: user.season?.totalXp || 0,
        rank: index + 1 + (lastUserId ? 50 : 0) // Approximate rank (not exact across pages)
      }
    })
  }
}
